import { createContext, useContext, useMemo } from 'react';
import { useGetTagAssignments } from '@utils/api/tag-assignments';
import { TagAssignment } from '@utils/types/organization/tag.ts';
import { findAssignmentForRange } from '@utils/tags/tag-helpers.ts';

type TagAssignmentsContextValue = {
  assignments: TagAssignment[];
  findAssignmentForRange: (
    start: number,
    stop: number
  ) => TagAssignment | undefined;
};

const TagAssignmentsContext = createContext<TagAssignmentsContextValue>({
  assignments: [],
  findAssignmentForRange: () => undefined
});

type ProviderProps = {
  sessionId: string;
  documentId: string;
  children: React.ReactNode;
};

export const TagAssignmentsProvider = ({
  sessionId,
  documentId,
  children
}: ProviderProps) => {
  const { data } = useGetTagAssignments({
    session_id: sessionId,
    document_id: documentId,
    enabled: !!sessionId && !!documentId
  });

  const value = useMemo<TagAssignmentsContextValue>(() => {
    const assignments = (data?.result ?? []) as TagAssignment[];
    return {
      assignments,
      findAssignmentForRange: (start: number, stop: number) =>
        findAssignmentForRange(assignments, start, stop)
    };
  }, [data]);

  return (
    <TagAssignmentsContext.Provider value={value}>
      {children}
    </TagAssignmentsContext.Provider>
  );
};

export const useTagAssignments = () => useContext(TagAssignmentsContext);
